import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageWrapper } from '../components/layout/PageWrapper';
import { Navbar } from '../components/layout/Navbar';
import { Card } from '../components/shared/Card';
import { Button } from '../components/shared/Button';
import { Input } from '../components/shared/Input';
import { CheckCircle, Circle, ArrowLeft } from 'lucide-react';

interface CriterionState {
  checked: boolean;
  adxValue?: string;
}

const criteria = [
  {
    id: 'adx',
    label: 'ADX Below 25',
    description: 'Confirm the ADX reading is below 25 — indicating a low-momentum environment.',
    hasInput: true,
    inputLabel: 'Current ADX',
  },
  {
    id: 'og',
    label: 'OG Signal (Both Panels)',
    description: 'OG Signal must fire on BOTH the price panel and the oscillator panel.',
    hasInput: false,
  },
  {
    id: 'choch',
    label: 'ChoCh OR CISD Present',
    description: 'A Change of Character or Change in State of Delivery must be confirmed.',
    hasInput: false,
  },
  {
    id: 'fvg',
    label: 'FVG Present',
    description: 'A Fair Value Gap (3-candle imbalance) must be present.',
    hasInput: false,
  },
  {
    id: 'fvg-fib',
    label: 'FVG at 50%+ Fib',
    description: 'The FVG must sit at 50% or higher on the Fibonacci retracement of the prior swing.',
    hasInput: false,
  },
  {
    id: 'fvg-confirmed',
    label: 'FVG Confirmed',
    description: 'Price has returned to the FVG and reacted — the zone is live.',
    hasInput: false,
  },
];

export function Checklist() {
  const navigate = useNavigate();
  const [states, setStates] = useState<Record<string, CriterionState>>(
    Object.fromEntries(criteria.map(c => [c.id, { checked: false }]))
  );

  const allChecked = criteria.every(c => states[c.id].checked);
  const checkedCount = criteria.filter(c => states[c.id].checked).length;

  function toggleCriterion(id: string) {
    setStates(prev => ({
      ...prev,
      [id]: { ...prev[id], checked: !prev[id].checked },
    }));
  }

  function setAdxValue(value: string) {
    const num = parseFloat(value);
    setStates(prev => ({
      ...prev,
      adx: { ...prev.adx, adxValue: value, checked: !isNaN(num) && num < 25 },
    }));
  }

  return (
    <PageWrapper warmth={0.35}>
      <Navbar />
      <main className="min-h-screen pt-16">
        <div className="max-w-[660px] mx-auto px-8 py-10">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-1 text-xs font-mono text-white/35 hover:text-white/70 mb-6 transition-colors duration-300 cursor-pointer"
          >
            <ArrowLeft className="w-3 h-3" />
            Back
          </button>

          <h1 className="text-2xl md:text-3xl font-bold font-mono tracking-wide text-shimmer mb-3">SMOG Pre-Trade Checklist</h1>
          <p className="text-[13px] font-mono text-white/35 mb-8 tracking-wide">
            All six criteria must be met before entering a SMOG trade. No partial credit.
          </p>

          {/* Progress bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs mb-1">
              <span className="text-white/40 font-mono">{checkedCount} of {criteria.length} criteria</span>
              {allChecked && (
                <span className="text-[#3DBF7A] font-semibold font-mono tracking-wider">ALL CRITERIA MET</span>
              )}
            </div>
            <div className="h-1 bg-white/[0.04] rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-500"
                style={{
                  width: `${(checkedCount / criteria.length) * 100}%`,
                  backgroundColor: allChecked ? '#3DBF7A' : 'rgba(255,255,255,0.25)',
                }}
              />
            </div>
          </div>

          {/* Criteria */}
          <div className="flex flex-col gap-3">
            {criteria.map((c, idx) => {
              const state = states[c.id];
              return (
                <Card
                  key={c.id}
                  bento
                  className={`!p-4 ${state.checked ? 'border-[#3DBF7A]/30' : ''}`}
                  style={{
                    animation: 'card-enter 0.5s cubic-bezier(0.16,1,0.3,1) both',
                    animationDelay: `${idx * 80}ms`,
                  }}
                >
                  <div className="flex items-start gap-3">
                    {c.id === 'adx' ? (
                      // ADX has special input handling
                      <div className={`shrink-0 mt-0.5 ${state.checked ? 'text-[#3DBF7A]' : 'text-white/40'}`}>
                        {state.checked ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                      </div>
                    ) : (
                      <button
                        onClick={() => toggleCriterion(c.id)}
                        className={`shrink-0 mt-0.5 ${state.checked ? 'text-[#3DBF7A]' : 'text-white/40'} hover:text-white transition-colors`}
                      >
                        {state.checked ? <CheckCircle className="w-5 h-5" /> : <Circle className="w-5 h-5" />}
                      </button>
                    )}

                    <div className="flex-1">
                      <span className={`text-base font-semibold font-mono ${state.checked ? 'text-[#3DBF7A]' : 'text-white'}`}>
                        {c.label}
                      </span>
                      <p className="text-sm font-mono text-white/35 mt-1 leading-relaxed">{c.description}</p>

                      {c.id === 'adx' && (
                        <div className="mt-2 max-w-[120px]">
                          <Input
                            label=""
                            type="number"
                            step="0.1"
                            placeholder="e.g. 18.5"
                            value={state.adxValue || ''}
                            onChange={e => setAdxValue(e.target.value)}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>

          {/* Actions */}
          <div className="mt-8 flex flex-col gap-3">
            <Button onClick={() => navigate('/prescription')} disabled={!allChecked}>
              {allChecked ? 'Ready to Trade' : `${criteria.length - checkedCount} criteria remaining`}
            </Button>
            <Button variant="ghost" onClick={() => navigate('/prescription')}>
              Back to Protocol
            </Button>
          </div>
        </div>
      </main>
    </PageWrapper>
  );
}
